import { SupplierApprovalStatus } from '@/models/common/ProjectStatus';
import { SupplierProjectApprovalDtoIn } from '@/models/in/ProjectSupplierApprovalDtoIn';

/**
 * Mirrors the backend SupplierTaxApprovalStatus enum. Values are strings (not
 * numbers) because the API serializes enums with JsonStringEnumConverter, so
 * each value MUST stay identical to the C# enum member name. Changing one of
 * these strings will silently break status comparisons (the supplier-approval
 * gate, the suppliers-management filter, the approval-status badges, etc.).
 */
export enum SupplierTaxApprovalStatus {
  NotRequired = 'NotRequired',
  Valid = 'Valid',
  PendingValidUntil = 'PendingValidUntil',
  PendingDocument = 'PendingDocument',
  Expired = 'Expired',
}

/**
 * Returns true if the supplier still needs attention from the secretariat -
 * i.e. withholding is configured but the approval isn't fully in place yet
 * or has expired. Used by the suppliers-management page filter and by the
 * support-request approval flow to decide whether to surface a warning dialog.
 */
export function isSupplierTaxApprovalActionable(status: SupplierTaxApprovalStatus): boolean {
  return (
    status === SupplierTaxApprovalStatus.PendingValidUntil ||
    status === SupplierTaxApprovalStatus.PendingDocument ||
    status === SupplierTaxApprovalStatus.Expired
  );
}

export interface FundRefDtoIn {
  id: number;
  name: string;
}

/**
 * Bank account previously registered for a supplier within a specific fund. Returned by the
 * fund-scoped suppliers endpoint so the new-request form can let the user pick a previously
 * used account or add a new one. Bank accounts from OTHER funds are never exposed.
 */
export interface SupplierPaymentInfoDtoIn {
  id: number;
  bankCode: number;
  bankBranch: string;
  bankAccount: string;
}

export interface SupplierFullDtoIn {
  id: number;
  name: string;
  taxId: string;
  phone?: string | null;
  email?: string | null;
  taxWithholdingPercentage?: number | null;
  taxApprovalDocumentId?: number | null;
  taxApprovalValidUntil?: string | null;
  taxApprovalStatus: SupplierTaxApprovalStatus;
  isArchived: boolean;
  createdAt: string;
  updatedAt?: string | null;
  /**
   * Funds this supplier is linked to (derived from existing support requests on the
   * backend). Populated by the suppliers-management list endpoint; may be missing
   * (treat as empty) in flows where the backend doesn't compute it.
   */
  funds?: FundRefDtoIn[];
  /**
   * Bank accounts already on file for this supplier within the current fund. Populated only
   * by the fund-scoped suppliers endpoint (used by the new-request form); empty/undefined
   * elsewhere.
   */
  paymentInfos?: SupplierPaymentInfoDtoIn[];
  /**
   * Approval lifecycle of the supplier (HAT-1105): PendingApproval for one a coordinator added and
   * the office has not looked at yet. Optional because only the endpoints written for the projects
   * feature are guaranteed to send it.
   */
  approvalStatus?: SupplierApprovalStatus;
  /** Activity domain from the closed list; null when the supplier has a free-text one or none. */
  activityDomainId?: number | null;
  /** Resolved name of the activity domain picked from the closed list. */
  activityDomainName?: string | null;
  /** Free-text domain, when the closed list did not fit ("אחר"). */
  activityDomainOther?: string | null;
  /**
   * Names of this fund's projects (מיזמים) the supplier is linked to. Populated only by the
   * fund-scoped suppliers endpoint.
   */
  projectNames?: string[];
  /**
   * SPR-10302: the supplier's approval on each of this fund's projects it is linked to - the office
   * approves a supplier per project. Populated only by the fund-scoped suppliers endpoint.
   */
  projectApprovals?: SupplierProjectApprovalDtoIn[];
}
